import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { api } from "../../common/setupApi";
import { wellnessFormat } from "../markers/format";

export const getTrashSessions = createAsyncThunk(
  "trash/getSessions",
  async (_, { rejectWithValue }) => {
    return api
      .get("deleted/session-types/")
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectTrashSessions(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

export const getTrashWellnessMarkers = createAsyncThunk(
  "trash/getWellnessMarkers",
  async (_, { rejectWithValue }) => {
    return api
      .get("deleted/wellness-questionnaire/")
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectTrashWellnessMarkers(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

export const getTrashSessionsMarkers = createAsyncThunk(
  "trash/getSessionsMarkers",
  async (_, { rejectWithValue }) => {
    return api
      .get("deleted/session-questionnaire/")
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectTrashSessionsMarkers(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

export const getTrashTags = createAsyncThunk(
  "trash/getTags",
  async (_, { rejectWithValue }) => {
    return api
      .get("deleted/tags/")
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectTrashTags(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

const initialState = {
  sessions: {
    status: "idle",
    error: null,
    data: [],
  },
  markers: {
    wellness: {
      status: "idle",
      error: null,
      data: {},
    },
    sessions: {
      status: "idle",
      error: null,
      data: {},
    },
  },
  tags: {
    status: "idle",
    error: null,
    data: [],
  },
};

const trashSlice = createSlice({
  name: "trash",
  initialState,
  reducers: {
    addMarker: (state, action) => {
      state.markers[action.payload.questionnaireType].data[
        action.payload.markerType
      ].push(action.payload.data);
    },
    deleteMarker: (state, action) => {
      const { questionnaireType, markerType, id } = action.payload;
      state.markers[questionnaireType].data[markerType] = state.markers[
        questionnaireType
      ].data[markerType].filter((d) => d.id !== id);
    },
    addTag: (state, action) => {
      state.tags.data.push(action.payload);
    },
    deleteTag: (state, action) => {
      state.tags.data = state.tags.data.filter((d) => d.id !== action.payload);
    },
    addSession: (state, action) => {
      state.sessions.data.push(action.payload);
    },
    deleteSession: (state, action) => {
      state.sessions.data = state.sessions.data.filter(
        (d) => d.id !== action.payload
      );
    },
    // ne pas suppr cette action : restoreSession est utilisée dans markersSlice
    restoreSession: (state, action) => {
      state.sessions.data = state.sessions.data.filter(
        (d) => d.id !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTrashSessions.pending, (state) => {
        state.sessions.status = "pending";
        state.sessions.error = null;
      })
      .addCase(getTrashSessions.fulfilled, (state, action) => {
        state.sessions.status = "fulfilled";
        state.sessions.data = action.payload;
      })
      .addCase(getTrashSessions.rejected, (state, action) => {
        state.sessions.status = "rejected";
        state.sessions.error = action.payload;
      })
      .addCase(getTrashWellnessMarkers.pending, (state) => {
        state.markers.wellness.status = "pending";
        state.markers.wellness.error = null;
      })
      .addCase(getTrashWellnessMarkers.fulfilled, (state, action) => {
        state.markers.wellness.status = "fulfilled";
        state.markers.wellness.data = action.payload;
      })
      .addCase(getTrashWellnessMarkers.rejected, (state, action) => {
        state.markers.wellness.status = "rejected";
        state.markers.wellness.error = action.payload;
      })
      .addCase(getTrashSessionsMarkers.pending, (state) => {
        state.markers.sessions.status = "pending";
        state.markers.sessions.error = null;
      })
      .addCase(getTrashSessionsMarkers.fulfilled, (state, action) => {
        state.markers.sessions.status = "fulfilled";
        state.markers.sessions.data = action.payload;
      })
      .addCase(getTrashSessionsMarkers.rejected, (state, action) => {
        state.markers.sessions.status = "rejected";
        state.markers.sessions.error = action.payload;
      })
      .addCase(getTrashTags.pending, (state) => {
        state.tags.status = "pending";
        state.tags.error = null;
      })
      .addCase(getTrashTags.fulfilled, (state, action) => {
        state.tags.status = "fulfilled";
        state.tags.data = action.payload;
      })
      .addCase(getTrashTags.rejected, (state, action) => {
        state.tags.status = "rejected";
        state.tags.error = action.payload;
      })
      .addCase("auth/logout", () => initialState);
  },
});

export const trashActions = trashSlice.actions;

const _selectTrashSessions = (state) => state.trash.sessions;

export const selectTrashSessions = createSelector(
  [(state) => _selectTrashSessions(state)],
  (sessions) => {
    const data = sessions.data;
    const status = sessions.status;
    const error = sessions.error;
    const isLoading = status === "pending";
    return { data, status, error, isLoading };
  }
);

const _selectTrashWellnessMarkers = (state) => state.trash.markers.wellness;

export const selectTrashWellnessMarkers = createSelector(
  [(state) => _selectTrashWellnessMarkers(state)],
  (wellness) => {
    const data = wellness.data;
    const status = wellness.status;
    const error = wellness.error;
    const isLoading = status === "pending";
    return {
      data,
      status,
      error,
      isLoading,
      perc: data.vas || [],
      scale: data.scale || [],
      quanti: data.indicator || [],
      duration: data.duration || [],
      bool: data.boolean || [],
      mcq: data.mcq || [],
      qst: data.question || [],
    };
  }
);

const _selectTrashSessionsMarkers = (state) => state.trash.markers.sessions;

export const selectTrashSessionsMarkers = createSelector(
  [(state) => _selectTrashSessionsMarkers(state)],
  (sessions) => {
    const data = sessions.data;
    const status = sessions.status;
    const error = sessions.error;
    const isLoading = status === "pending";
    return {
      data,
      status,
      error,
      isLoading,
      perc: data.vas || [],
      scale: data.scale || [],
      quanti: data.indicator || [],
      duration: data.duration || [],
      bool: data.boolean || [],
      mcq: data.mcq || [],
      qst: data.question || [],
    };
  }
);

// memo ok
export const selectTrashMarkers = createSelector(
  [
    (state) => selectTrashWellnessMarkers(state).perc,
    (state) => selectTrashSessionsMarkers(state).perc,
    (state) => selectTrashWellnessMarkers(state).scale,
    (state) => selectTrashSessionsMarkers(state).scale,
    (state) => selectTrashWellnessMarkers(state).quanti,
    (state) => selectTrashSessionsMarkers(state).quanti,
    (state) => selectTrashWellnessMarkers(state).duration,
    (state) => selectTrashSessionsMarkers(state).duration,
    (state) => selectTrashWellnessMarkers(state).bool,
    (state) => selectTrashSessionsMarkers(state).bool,
    (state) => selectTrashWellnessMarkers(state).mcq,
    (state) => selectTrashSessionsMarkers(state).mcq,
    (state) => selectTrashWellnessMarkers(state).qst,
    (state) => selectTrashSessionsMarkers(state).qst,
  ],
  (
    wellnessPerc,
    sessionsPerc,
    wellnessScale,
    sessionsScale,
    wellnessQuanti,
    sessionsQuanti,
    wellnessDuration,
    sessionsDuration,
    wellnessBool,
    sessionsBool,
    wellnessMcq,
    sessionsMcq,
    wellnessQst,
    sessionsQst
  ) => {
    const perc = wellnessFormat(wellnessPerc).concat(sessionsPerc);
    const scale = wellnessFormat(wellnessScale).concat(sessionsScale);
    const quanti = wellnessFormat(wellnessQuanti).concat(sessionsQuanti);
    const duration = wellnessFormat(wellnessDuration).concat(sessionsDuration);
    const bool = wellnessFormat(wellnessBool).concat(sessionsBool);
    const mcq = wellnessFormat(wellnessMcq).concat(sessionsMcq);
    const qst = wellnessFormat(wellnessQst).concat(sessionsQst);
    return {
      data: perc
        .concat(scale)
        .concat(quanti)
        .concat(duration)
        .concat(bool)
        .concat(mcq)
        .concat(qst),
      perc,
      scale,
      quanti,
      duration,
      bool,
      mcq,
      qst,
    };
  }
);

const _selectTrashTags = (state) => state.trash.tags;

export const selectTrashTags = createSelector(
  [(state) => _selectTrashTags(state)],
  (tags) => {
    const data = tags.data;
    const status = tags.status;
    const error = tags.error;
    const isLoading = status === "pending";
    return { data, status, error, isLoading };
  }
);

export default trashSlice.reducer;
